import { FileBarChart, ClipboardList, FileSearch, FileCheck2, PackageCheck, AlertTriangle, Building2 } from "lucide-react";
import type { ComponentType } from "react";
import { Link } from "react-router-dom";
import {
  requisitionApi,
  rfqApi,
  lpoApi,
  grnApi,
  ncrApi,
  supplierApi,
} from "../api";
import { ProcError, ProcLoading } from "../components/ProcurementLayout";
import { ProcStatusBadge } from "../components/ProcStatusBadge";
import { useAsyncData } from "../hooks";

const CARD_THEMES = {
  emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-700", ring: "hover:ring-emerald-200", value: "text-emerald-900" },
  blue: { bg: "bg-blue-50", icon: "bg-blue-100 text-blue-700", ring: "hover:ring-blue-200", value: "text-blue-900" },
  purple: { bg: "bg-purple-50", icon: "bg-purple-100 text-purple-700", ring: "hover:ring-purple-200", value: "text-purple-900" },
  amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-700", ring: "hover:ring-amber-200", value: "text-amber-900" },
  red: { bg: "bg-red-50", icon: "bg-red-100 text-red-700", ring: "hover:ring-red-200", value: "text-red-900" },
  teal: { bg: "bg-teal-50", icon: "bg-teal-100 text-teal-700", ring: "hover:ring-teal-200", value: "text-teal-900" },
} as const;

type ThemeKey = keyof typeof CARD_THEMES;

export default function ProcurementDashboard() {
  const { data: stats, loading, error } = useAsyncData(async () => {
    const [reqs, rfqs, lpos, grns, ncrs, suppliers] = await Promise.all([
      requisitionApi.list({ page: 0, size: 1 }),
      rfqApi.list({ page: 0, size: 1 }),
      lpoApi.list({ page: 0, size: 1 }),
      grnApi.list({ page: 0, size: 1 }),
      ncrApi.list({ page: 0, size: 1 }),
      supplierApi.list({ page: 0, size: 1 }),
    ]);
    return {
      requisitions: reqs.totalElements,
      rfqs: rfqs.totalElements,
      lpos: lpos.totalElements,
      grns: grns.totalElements,
      ncrs: ncrs.totalElements,
      suppliers: suppliers.totalElements,
    };
  }, []);

  const { data: recent } = useAsyncData(
    () => requisitionApi.list({ page: 0, size: 5 }),
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Procurement Dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/procurement/requisitions/new" className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
            New Requisition
          </Link>
          <Link to="/procurement/suppliers" className="rounded-xl border border-emerald-700 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
            Suppliers
          </Link>
          <Link to="/procurement/lpos" className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            LPOs
          </Link>
          <Link to="/procurement/reports" className="flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <FileBarChart className="h-4 w-4" />
            Reports
          </Link>
        </div>
      </div>

      {loading && <ProcLoading label="Loading dashboard..." />}
      {error && <ProcError message={error} />}

      {!loading && !error && stats && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label="Requisitions" value={stats.requisitions} to="/procurement/requisitions" icon={ClipboardList} theme="emerald" />
            <StatCard label="RFQs" value={stats.rfqs} to="/procurement/rfqs" icon={FileSearch} theme="blue" />
            <StatCard label="LPOs" value={stats.lpos} to="/procurement/lpos" icon={FileCheck2} theme="purple" />
            <StatCard label="GRNs" value={stats.grns} to="/procurement/grns" icon={PackageCheck} theme="teal" />
            <StatCard label="NCRs" value={stats.ncrs} to="/procurement/ncrs" icon={AlertTriangle} theme="red" />
            <StatCard label="Suppliers" value={stats.suppliers} to="/procurement/suppliers" icon={Building2} theme="amber" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <QuickLink to="/procurement/requisitions/new" title="New Requisition" desc="Create a purchase requisition" theme="emerald" />
            <QuickLink to="/procurement/suppliers" title="Supplier Register" desc="View and manage suppliers" theme="amber" />
            <QuickLink to="/procurement/lpos" title="Local Purchase Orders" desc="View and create LPOs" theme="purple" />
            <QuickLink to="/procurement/supplier-prequal" title="Prequalification" desc="Supplier prequalification register" theme="blue" />
          </div>
        </>
      )}

      {recent && recent.content.length > 0 && (
        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <div>
              <h2 className="font-bold text-slate-900">Recent Requisitions</h2>
              <p className="text-xs text-slate-500">Last 5 purchase requisitions</p>
            </div>
            <Link to="/procurement/requisitions" className="text-sm font-semibold text-emerald-700 hover:underline">View all</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Serial No</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">Purpose</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recent.content.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.serialNo}</td>
                    <td className="px-4 py-3 text-slate-600">{r.departmentName}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{r.purpose}</td>
                    <td className="px-4 py-3"><ProcStatusBadge value={r.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/procurement/requisitions/${r.id}`} className="font-semibold text-emerald-700 hover:underline">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  to,
  icon: Icon,
  theme,
}: {
  label: string;
  value: number;
  to: string;
  icon: ComponentType<{ className?: string }>;
  theme: ThemeKey;
}) {
  const t = CARD_THEMES[theme];
  return (
    <Link
      to={to}
      className={`flex items-center gap-4 rounded-2xl border ${t.bg} p-5 shadow-sm ring-1 ring-transparent transition hover:-translate-y-0.5 hover:shadow-md ${t.ring}`}
    >
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${t.icon}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-0.5 text-3xl font-bold ${t.value}`}>{value.toLocaleString()}</div>
      </div>
    </Link>
  );
}

function QuickLink({ to, title, desc, theme }: { to: string; title: string; desc: string; theme: ThemeKey }) {
  const t = CARD_THEMES[theme];
  return (
    <Link
      to={to}
      className={`rounded-2xl border ${t.bg} p-5 shadow-sm ring-1 ring-transparent transition hover:-translate-y-0.5 hover:shadow-md ${t.ring} block`}
    >
      <div className="font-bold text-slate-900">{title}</div>
      <p className="mt-1 text-sm text-slate-600">{desc}</p>
    </Link>
  );
}
